import { Button, MarkdownPreview } from '@consigli/facade';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface TemplateEditorProps {
  templateContent: string;
  editedContent: string;
  isEditMode: boolean;
  hasUnsavedChanges: boolean;
  isLoadingTemplate: boolean;
  onEditChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onStartEdit: () => void;
  onRunPrecheck: () => void;
  isStartingPipeline: boolean;
}

const extractTableOfContents = (markdown: string): string => {
  const betweenDelimiters = /---\s*\n\n## Structure Template\s*\n([\s\S]*?)\n---/i.exec(markdown);
  if (betweenDelimiters) {
    let content = betweenDelimiters[1].trim();
    const justificationIndex = content.indexOf('Justification:');
    if (justificationIndex !== -1) {
      content = content.substring(0, justificationIndex).trim();
    }
    return content;
  }

  const fromHeading = /## Structure Template\s*\n([\s\S]*?)(?=\n##[^#]|\n#[^#]|$)/i.exec(markdown);
  if (fromHeading) {
    let content = fromHeading[1].trim();
    const justificationIndex = content.indexOf('Justification:');
    if (justificationIndex !== -1) {
      content = content.substring(0, justificationIndex).trim();
    }
    return content;
  }

  let content = markdown;
  const justificationIndex = content.indexOf('Justification:');
  if (justificationIndex !== -1) {
    content = content.substring(0, justificationIndex).trim();
  }
  return content;
};

const reconstructMarkdown = (originalMarkdown: string, newToc: string): string => {
  const withDelimiters = /---\s*\n\n## Structure Template\s*\n([\s\S]*?)\n---/i;
  if (withDelimiters.test(originalMarkdown)) {
    return originalMarkdown.replace(withDelimiters, `---\n\n## Structure Template\n${newToc}\n---`);
  }

  const withHeading = /(## Structure Template\s*\n)([\s\S]*?)(?=\n##[^#]|\n#[^#]|$)/i;
  if (withHeading.test(originalMarkdown)) {
    return originalMarkdown.replace(withHeading, `$1${newToc}\n`);
  }

  return newToc;
};

export const TemplateEditor: FC<TemplateEditorProps> = ({
  templateContent,
  editedContent,
  isEditMode,
  hasUnsavedChanges,
  isLoadingTemplate,
  onEditChange,
  onSave,
  onCancel,
  onStartEdit,
  onRunPrecheck,
  isStartingPipeline,
}) => {
  const { t } = useTranslation();
  const fullContent = editedContent || templateContent;
  const tocContent = extractTableOfContents(fullContent);

  const handleTocChange = (newToc: string) => {
    const updatedFullMarkdown = reconstructMarkdown(fullContent, newToc);
    onEditChange(updatedFullMarkdown);
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="mb-6 text-center">
        <h1 className="text-2xl font-semibold text-day-neutral-dark mb-2">
          {t('package-creation.reports.template-check-title', 'Template Analysis Complete')}
        </h1>
        <p className="text-day-neutral-subtle">
          {t(
            'package-creation.reports.review-table-of-contents',
            'Review the table of contents for your report template',
          )}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-day-light-2">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-day-neutral-dark">
              {t('package-creation.reports.table-of-contents', 'Table of Contents')}
            </h2>
            {hasUnsavedChanges && (
              <span className="text-xs px-2 py-1 bg-orange-100 text-orange-700 rounded-full font-medium">
                {t('package-creation.reports.unsaved-changes', 'Unsaved changes')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isEditMode ? (
              <>
                <Button rounded secondary onClick={onCancel} className="text-sm">
                  {t('package-creation.reports.cancel', 'Cancel')}
                </Button>
                <Button
                  rounded
                  primary
                  onClick={onSave}
                  disabled={!hasUnsavedChanges}
                  className="text-sm"
                >
                  {t('package-creation.reports.save', 'Save Changes')}
                </Button>
              </>
            ) : (
              <Button rounded secondary onClick={onStartEdit} className="text-sm">
                {t('package-creation.reports.edit', 'Edit')}
              </Button>
            )}
          </div>
        </div>

        {isEditMode ? (
          <div className="space-y-2">
            <textarea
              value={tocContent}
              onChange={(e) => handleTocChange(e.target.value)}
              className="w-full h-[60vh] max-h-[600px] p-4 border border-day-light-2 rounded-md font-mono text-sm text-day-neutral-dark focus:outline-none focus:ring-2 focus:ring-brand-default resize-none overflow-y-auto"
              placeholder={t(
                'package-creation.reports.edit-table-of-contents-placeholder',
                'Edit table of contents...',
              )}
            />
            <p className="text-xs text-day-neutral-subtle">
              {t(
                'package-creation.reports.edit-table-of-contents-hint',
                'Edit the table of contents. Other sections will be preserved automatically.',
              )}
            </p>
          </div>
        ) : isLoadingTemplate ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-day-neutral-subtle">
              {t('package-creation.reports.loading-template', 'Loading template...')}
            </p>
          </div>
        ) : (
          <div className="prose prose-sm max-w-none h-[60vh] max-h-[600px] overflow-y-auto">
            <MarkdownPreview content={tocContent} />
          </div>
        )}
      </div>

      <div className="flex justify-center">
        <Button
          rounded
          primary
          onClick={onRunPrecheck}
          disabled={hasUnsavedChanges || isStartingPipeline}
        >
          {isStartingPipeline
            ? t('package-creation.reports.starting', 'Starting...')
            : t('package-creation.reports.run-precheck', 'Run Precheck')}
        </Button>
      </div>
    </div>
  );
};
